import { useEffect, useMemo, useState } from 'react';

import { FormikProps, FormikValues, useFormik } from 'formik';
import { DateTime } from 'luxon';

import {
  AdminLegendVariant,
  getLongTermContractAttributes,
  PharmacistLegendVariant,
  setLongTermContractAttributes,
  WorkshiftStatus,
} from '@pharmaplan/common';

import useGenerateContractRange from '../../LongTermContract/useGenerateContractRange';
import useViewEditContract from '../../workshift/useViewEditContract';
import useExpressBookingTypes from './useExpressBookingTypes';
import { resetDialog } from '../../../reducers/dialogReducer';
import { useAppSelector } from '../../useAppSelector';
import { useAppDispatch } from '../../useAppDispatch';

const useExpressBookingLongTermModal = (
  type: AdminLegendVariant | PharmacistLegendVariant,
  formFormik: FormikProps<any>,
  isUpdate: boolean,
) => {
  const dispatch = useAppDispatch();
  const { detailsApiMap } = useExpressBookingTypes();
  const { details } = detailsApiMap[type as keyof typeof detailsApiMap] ?? {};

  const { getInitialShiftAttributes } = useGenerateContractRange();
  const { allShifts } = details ?? {};
  const onlyAvailableShifts = useMemo(() =>
    allShifts?.filter((item) =>
      item.status !== WorkshiftStatus.Booked), [allShifts]);

  const { intervalsInitial, getFormattedObj, generateTimeIntervals } = useViewEditContract();

  const longTermAttributes = useAppSelector(getLongTermContractAttributes);
  const {
    startDate: savedStartDate,
    endDate: savedEndDate,
    defaultEndTime: savedDefaultEndTime,
    defaultStartTime: savedDefaultStartTime,
    shiftContractAttributes,
  } = longTermAttributes ?? {};

  const [selectedIds, setSelectedIds] = useState<Array<string>>(
    formFormik.values?.workshiftIds ?? [],
  );

  const isAllSelected = selectedIds?.length === onlyAvailableShifts?.length;

  const onSubmit = (values: FormikValues) => {
    const { startDate, endDate, defaultStartTime, defaultEndTime } = values ?? {};

    const formattedObj = getFormattedObj(values);
    console.log(
      startDate,
      endDate,
      defaultEndTime,
      defaultStartTime,
      formattedObj,
    );
    dispatch(
      setLongTermContractAttributes({
        shiftContractAttributes: Object.values(formattedObj),
        startDate,
        endDate,
        defaultEndTime,
        defaultStartTime,
      }),
    );

    formFormik.setFieldValue('workshiftIds', selectedIds);
    dispatch(resetDialog());
  };

  const formik = useFormik({
    initialValues: {
      startDate: savedStartDate ?? null,
      endDate: savedEndDate ?? null,
      defaultStartTime:
        savedDefaultStartTime
        ?? DateTime.now().set({ hour: 9, minute: 0, second: 0 }).toJSDate(),
      defaultEndTime:
        savedDefaultEndTime
        ?? DateTime.now().set({ hour: 17, minute: 0, second: 0 }).toJSDate(),
    },
    onSubmit,
  });

  const handleCheckboxClick = (shiftId: string) =>
    () => {
      if (selectedIds.includes(shiftId)) {
        setSelectedIds(selectedIds.filter((item) =>
          item !== shiftId));
      } else {
        setSelectedIds((prev) =>
          [...prev, shiftId]);
      }
    };

  const handleClose = () => {
    dispatch(resetDialog());
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(() =>
        (onlyAvailableShifts ?? []).map((item) =>
          item.workShiftId ?? ''));
    }
  };

  const onDateChange = (
    startDate: DateTime | string,
    endDate: DateTime | string,
  ) => {
    const shiftAttributes = getInitialShiftAttributes({
      startDate: startDate as DateTime,
      endDate: endDate as DateTime,
      startTime:
        DateTime.fromJSDate(formik.values.defaultStartTime as Date).toISO({
          includeOffset: false,
        }) ?? '',
      endTime:
        DateTime.fromJSDate(formik.values.defaultEndTime as Date).toISO({
          includeOffset: false,
        }) ?? '',
    });

    if (shiftAttributes?.length) {
      formik.setValues({
        startDate: formik.values.startDate,
        endDate: formik.values.endDate,
        defaultStartTime: formik.values.defaultStartTime,
        defaultEndTime: formik.values.defaultEndTime,
        ...generateTimeIntervals(shiftAttributes)(
          formik.values?.defaultStartTime as Date,
          formik.values?.defaultEndTime as Date,
        ),
      });

      dispatch(
        setLongTermContractAttributes({
          ...longTermAttributes,
          shiftContractAttributes: shiftAttributes ?? [],
        }),
      );
    }
  };

  useEffect(() => {
    if (shiftContractAttributes?.length) {
      formik.setValues({
        startDate: formik.values.startDate,
        endDate: formik.values.endDate ?? null,
        defaultStartTime:
          formik.values.defaultStartTime,
        defaultEndTime:
          formik.values.defaultEndTime,
        ...intervalsInitial(shiftContractAttributes ?? []),

      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftContractAttributes, savedEndDate, savedStartDate]);

  return {
    formik,
    handleCheckboxClick,
    handleClose,
    handleSelectAll,
    disabled: selectedIds.length === 0,
    isAllSelected,
    data: isUpdate ? allShifts : shiftContractAttributes,
    selectedIds,
    onDateChange,
  };
};

export default useExpressBookingLongTermModal;
